/**
 * @fileoverview PDS 用 Cart Ajax API ヘルパー（429 リトライ・GET 重複排除・busy 待機）。
 */
(function () {
  /** @type {Promise<object>|null} */
  let cartJsonInFlight = null;
  /** @type {object|null} */
  let cartJsonCache = null;
  let cartJsonCacheAt = 0;
  const CART_JSON_CACHE_MS = 450;

  /**
   * @param {number} attempt
   * @returns {Promise<void>}
   */
  function delayForRateLimit(attempt) {
    return new Promise((resolve) => setTimeout(resolve, 350 * attempt));
  }

  /**
   * @param {string} url
   * @param {RequestInit} options
   * @param {number} [maxAttempts]
   * @returns {Promise<Response>}
   */
  async function fetchWithRateLimitRetry(url, options, maxAttempts = 3) {
    let attempt = 0;
    while (attempt < maxAttempts) {
      attempt += 1;
      const response = await fetch(url, options);
      if (response.status !== 429) {
        return response;
      }
      if (attempt >= maxAttempts) {
        return response;
      }
      await delayForRateLimit(attempt);
    }
    throw new Error('Cart request failed after retries');
  }

  /**
   * 直近の cart.js キャッシュを破棄する（POST 反映後に呼ぶ）。
   */
  function invalidateCartJsonCache() {
    cartJsonCache = null;
    cartJsonCacheAt = 0;
  }

  /**
   * main-cart-items / ドロワーが Ajax 更新中か。
   * @returns {boolean}
   */
  function isCartAjaxBusy() {
    const main = document.getElementById('main-cart-items');
    const drawer = document.getElementById('CartDrawer-CartItems');
    return (
      (main && main.classList.contains('cart__items--disabled')) ||
      (drawer && drawer.classList.contains('cart__items--disabled'))
    );
  }

  /**
   * Cart JSON を取得（進行中リクエストの共有・短時間キャッシュ・429 リトライ）。
   * @param {{ maxAttempts?: number, maxAge?: number }} [opts]
   * @returns {Promise<object>}
   */
  async function fetchCartJson(opts = {}) {
    const maxAttempts = opts.maxAttempts ?? 3;
    const maxAge = opts.maxAge ?? CART_JSON_CACHE_MS;
    const now = Date.now();

    if (cartJsonCache && now - cartJsonCacheAt < maxAge) {
      return cartJsonCache;
    }

    if (cartJsonInFlight) {
      return cartJsonInFlight;
    }

    const url = (window.routes?.cart_url || '/cart') + '.js';
    cartJsonInFlight = (async () => {
      try {
        const response = await fetchWithRateLimitRetry(
          url,
          { method: 'GET', headers: { Accept: 'application/json' } },
          maxAttempts
        );
        if (!response.ok) {
          throw new Error('Cart GET failed: ' + response.status);
        }
        const data = await response.json();
        cartJsonCache = data;
        cartJsonCacheAt = Date.now();
        return data;
      } finally {
        cartJsonInFlight = null;
      }
    })();

    return cartJsonInFlight;
  }

  /**
   * JSON POST（cart/update.js 等）。429 時はリトライ。
   * @param {string} url
   * @param {object} body
   * @param {{ maxAttempts?: number, headers?: Record<string, string> }} [opts]
   * @returns {Promise<Response>}
   */
  async function postCartJson(url, body, opts = {}) {
    const response = await fetchWithRateLimitRetry(
      url,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(opts.headers || {}),
        },
        body: JSON.stringify(body),
      },
      opts.maxAttempts ?? 3
    );
    if (response.ok) {
      invalidateCartJsonCache();
    }
    return response;
  }

  /**
   * カート行の Ajax 更新が終わるまで待つ（タイムアウトで打ち切り）。
   * @param {number} [timeoutMs]
   * @returns {Promise<void>}
   */
  function waitForCartAjaxIdle(timeoutMs = 2500) {
    const start = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        if (!isCartAjaxBusy() || Date.now() - start >= timeoutMs) {
          resolve();
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  window.pdsInvalidateCartJsonCache = invalidateCartJsonCache;
  window.pdsFetchCartJson = fetchCartJson;
  window.pdsFetchWithRateLimitRetry = fetchWithRateLimitRetry;
  window.pdsPostCartJson = postCartJson;
  window.pdsIsCartAjaxBusy = isCartAjaxBusy;
  window.pdsWaitForCartAjaxIdle = waitForCartAjaxIdle;
})();
